import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Container,
  Flex,
  Group,
  Loader,
  Progress,
  Space,
  Text
} from '@mantine/core';
import { Modal } from '@components/modal';
import { Controls } from '@components/modal/components/controls';
import { IRemoveUser, IUserTableData } from '@/entities/admin-app/users';
import { useAppSelector } from '@hooks/redux/redux';
import { ListItem } from './list-tem';
import { useStyles } from './styles';
import { TransferData } from './transfer-data';
import { useDeleteUsers } from '@hooks/users/useDeleteUsers';
import { IRowData } from '@components/simple-table/types';
import { useActions } from '@/hooks/redux/action';
import { useTranslation } from 'react-i18next';
import { RoleTypesEnum } from '@/types/enums/user';

interface IDeleteUserProps {
  isOpened: boolean;
  selectedUsers: number[];
  handleClose: () => void;
  setRowData: React.Dispatch<React.SetStateAction<IRowData[]>>;
  scrollRef?: any;
}
export const DeleteUser = ({
  selectedUsers,
  isOpened,
  handleClose,
  setRowData,
  scrollRef
}: IDeleteUserProps) => {
  const { t } = useTranslation();
  const [userList, setUserList] = useState<IRemoveUser[]>([]);
  const [transferUser, setTransferUser] = useState<number | null>(null);
  const { classes } = useStyles();
  const { users } = useAppSelector((state) => state.users);
  const { User } = useAppSelector((state) => state.userInfo);
  const isMounted = useRef(true);
  const { handleDeleteUsers, modalLoadingStatus, percentageComplete } = useDeleteUsers(
    setRowData,
    isMounted,
    scrollRef
  );
  const activeCheckbox = useMemo(
    () => userList.filter((user) => user.isChecked && !user.isNotAcceptable),
    [userList]
  );

  const activeCheckboxLength = activeCheckbox.length;
  const selectedUserLength = selectedUsers?.length;
  const { setSelectedUserId } = useActions();
  const skipIdsForTransfer = userList
    .filter((user) => !user.isNotAcceptable)
    .map((item) => item?.id);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const canDeleteUser = (userForDelete: IUserTableData) => {
    return (
      User?.Id !== userForDelete?.Id && !userForDelete?.RoleTypes.includes(RoleTypesEnum.SuperUser)
    );
  };

  const handleChange = (user: IRemoveUser) => {
    setUserList((prevUserList) =>
      prevUserList.map((state) => {
        if (state.id === user.id) {
          return user;
        }
        return state;
      })
    );
  };

  const handleDeleteUser = async () => {
    const data = activeCheckbox.map((object) => {
      let transferId: number[] = transferUser ? [transferUser] : [];

      if (object.transferUserId) {
        transferId = [object.transferUserId];
      }

      return {
        UserId: object.id,
        TransferUserId: transferId
      };
    });
    try {
      await handleDeleteUsers(data);
      setSelectedUserId(0);
      handleClose();
    } catch (error) {
      console.error('Error:', error);
    }
  };
  const handleSelectedAll = () => {
    setUserList((prevList) => {
      let isChecked = true;
      if (activeCheckbox.some((active) => active.isChecked)) {
        isChecked = false;
      }

      return prevList.map((list) => ({
        ...list,
        isChecked: list?.isNotAcceptable ? false : isChecked
      }));
    });
  };

  useEffect(() => {
    const data: IRemoveUser[] = users
      .filter((user) => selectedUsers?.includes(user.Id))
      .map((filtered) => ({
        isNotAcceptable: !canDeleteUser(filtered),
        isChecked: canDeleteUser(filtered),
        id: filtered.Id,
        FirstName: filtered?.FirstName,
        SurName: filtered?.Surname,
        email: filtered?.Email,
        transferUserId: null,
        Avatar: filtered?.Avatar
      }));

    setUserList(data);
  }, [selectedUsers]);

  const closeModal = (e?: any) => {
    e?.preventDefault();
    e?.stopPropagation();
    handleClose();
  };

  return (
    <Modal opened={isOpened} size="lg" title={t('users.delete')} onClose={closeModal} withinPortal>
      {modalLoadingStatus && (
        <Box className={classes.box}>
          <Loader />
          {selectedUserLength > 2 && (
            <Progress value={percentageComplete} className={classes.progress} />
          )}
          <Text c="brandGray.0" fz="sm">
            {t('users.deleteTime')}
          </Text>
        </Box>
      )}
      <Container className={modalLoadingStatus ? classes.transparent : ''} px="md" pt={12}>
        <Text fz="sm" mb="sm">
          {t('users.deleteSelected')}
        </Text>
        <Text fz="sm" lh="17px" mb="md">
          {t('users.deleteInfo')}
        </Text>
        <Text c="brandGray.0" fz="xs" mb={12} lh={1}>
          {t('users.chooseTransfer')}
        </Text>
        <TransferData
          userId={-1}
          nameButton={t('users.userTransfer')}
          onChange={setTransferUser}
          SkipUsersIds={skipIdsForTransfer}
        />
        <Space h="sm" />
        <ul className={classes.list}>
          <li className={classes.header}>
            <Flex className={classes.headerElem}>
              <Flex gap="xs" className={classes.select}>
                <Checkbox
                  checked={activeCheckboxLength === selectedUserLength}
                  indeterminate={
                    !!activeCheckboxLength && activeCheckboxLength !== selectedUserLength
                  }
                  size="xs"
                  onChange={handleSelectedAll}
                />
                <Text fz="xs" c="brandGray.0">
                  {t('profile.user')}
                </Text>
              </Flex>
              <Flex className={classes.transferInfo}>
                <Text fz="xs" c="brandGray.0">
                  {t('users.userTransfer')}
                </Text>
              </Flex>
            </Flex>
          </li>
          {userList?.map((user) => (
            <li key={user.id}>
              <ListItem
                isSelectedMultiple
                value={user}
                onChange={handleChange}
                SkipUsersIds={[user?.id, ...skipIdsForTransfer]}
              />
            </li>
          ))}
        </ul>
        <Space h="sm" />
        <Text fz="sm" lh="17px" mb="md">
          {t('users.transferInfo')}
        </Text>
      </Container>
      <Controls>
        <Group className={modalLoadingStatus ? classes.transparent : ''} grow>
          <Button variant="default" bg="brandGray.1" onClick={closeModal}>
            {t('cancel')}
          </Button>
          <Button
            disabled={!activeCheckboxLength}
            color="red"
            bg="#C23939"
            onClick={handleDeleteUser}
          >
            {t('delete')} {activeCheckboxLength ? `(${activeCheckboxLength})` : ''}
          </Button>
        </Group>
      </Controls>
    </Modal>
  );
};
